import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { Download, Eye, LayoutGrid, Loader2, Table2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { downloadFormSubmissionsCsv, dynamicFormsApi } from '@/lib/api/dynamic-forms';
import {
  formatAppointmentRangeInZone,
  formatIsoInScheduleZone,
  resolveScheduleTimeZone,
  scheduleTimeZoneFromField,
} from '@/lib/schedule-timezone';
import type {
  FormSubmissionColumn,
  FormSubmissionDetail,
  FormSubmissionsListData,
} from '@/types/dynamic-form';
import type { FormField } from '@/types/form-definition';
import { formatAdminBusinessEt } from '@/lib/admin-datetime';
import { cn } from '@/lib/utils';

/** Same width on `th` / `td` so labels align; `max-w` caps runaway columns. */
const SUBMISSIONS_DATA_COL_CLASS =
  'min-w-[100px] max-w-[min(220px,30vw)] sm:max-w-[min(240px,22vw)]';

const SUBMISSIONS_LIST_VIEW_KEY = 'join-admin:form-submissions-list-view';

function pickSubmitterString(o: Record<string, unknown>, keys: string[]): string | null {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return null;
}

/** Primary line for card header (name, email, or phone). */
function submitterCardTitle(submitter: Record<string, unknown> | undefined): string {
  if (!submitter) return 'Submission';
  return (
    pickSubmitterString(submitter, [
      'fullName',
      'full_name',
      'name',
      'displayName',
      'firstName',
      'email',
      'Email',
      'cellPhoneNumber',
      'phone',
      'Phone',
    ]) ?? 'Submission'
  );
}

/** Secondary line: email under name, or phone when useful. */
function submitterCardSubtitle(submitter: Record<string, unknown> | undefined): string | null {
  if (!submitter) return null;
  const title = submitterCardTitle(submitter);
  const email = pickSubmitterString(submitter, ['email', 'Email']);
  const phone = pickSubmitterString(submitter, ['cellPhoneNumber', 'phone', 'Phone']);
  if (email && email !== title) return email;
  if (phone && phone !== title) return phone;
  return null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

/** Appointment answer may be `{ slotId }` only or `{ slotId, start, end }` after server enrichment. */
/** List/export cells may stringify objects. */
function unwrapMaybeJsonString(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const s = value.trim();
  if (!s.startsWith('{') && !s.startsWith('[')) return value;
  try {
    return JSON.parse(s) as unknown;
  } catch {
    return value;
  }
}

function looksLikeAppointmentValue(value: unknown): boolean {
  const v = unwrapMaybeJsonString(value);
  if (!isRecord(v)) return false;
  return 'slotId' in v || 'start' in v || 'end' in v;
}

const ADDRESS_TABLE_KEYS = ['street', 'street2', 'city', 'state', 'zip', 'country'] as const;

function looksLikeAddressShape(value: unknown): boolean {
  if (!isRecord(value)) return false;
  if (looksLikeAppointmentValue(value)) return false;
  const o = value as Record<string, unknown>;
  const filled = ADDRESS_TABLE_KEYS.filter(
    (k) => typeof o[k] === 'string' && (o[k] as string).trim().length > 0
  ).length;
  return filled >= 2;
}

/** One-line address for submissions table (detail view uses multi-line). */
function formatAddressForTable(value: unknown): string {
  const v = unwrapMaybeJsonString(value);
  if (!isRecord(v)) return formatScalarForTable(value);
  const o = v as Record<string, unknown>;
  const pick = (k: string) => (typeof o[k] === 'string' ? o[k].trim() : '');
  const street = pick('street');
  const street2 = pick('street2');
  const city = pick('city');
  const state = pick('state');
  const zip = pick('zip');
  const country = pick('country');
  const line1 = [street, street2].filter(Boolean).join(', ');
  const stateZip = [state, zip].filter(Boolean).join(' ');
  const mid = [city, stateZip].filter(Boolean).join(', ');
  const parts = [line1, mid, country].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : formatScalarForTable(value);
}

function formatAppointmentCell(value: unknown, defaultTimeZone: string): string {
  const raw = unwrapMaybeJsonString(value);
  if (!isRecord(raw)) return formatScalarForTable(value);
  const o = raw as Record<string, unknown>;
  const start = typeof o.start === 'string' ? o.start : null;
  const end = typeof o.end === 'string' ? o.end : null;
  const slotId = typeof o.slotId === 'string' ? o.slotId : null;
  const tz = resolveScheduleTimeZone(
    typeof o.scheduleTimeZone === 'string' ? o.scheduleTimeZone : undefined,
    defaultTimeZone
  );
  if (start) {
    if (end) return formatAppointmentRangeInZone(start, end, tz);
    return formatIsoInScheduleZone(start, tz);
  }
  if (slotId) return slotId;
  return formatScalarForTable(o);
}

function formatScalarForTable(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') {
    try {
      const s = JSON.stringify(value);
      return s.length > 140 ? `${s.slice(0, 137)}…` : s;
    } catch {
      return String(value);
    }
  }
  const s = String(value);
  return s.length > 220 ? `${s.slice(0, 217)}…` : s;
}

/** Data URL or https URL suitable for <img src>. */
function isRenderableSignatureSrc(s: string): boolean {
  const t = s.trim();
  if (/^data:image\//i.test(t)) return true;
  if (t.startsWith('http://') || t.startsWith('https://') || t.startsWith('/')) return true;
  return false;
}

function extractSignatureImageSrc(value: unknown): string | null {
  const v = unwrapMaybeJsonString(value);
  if (typeof v === 'string' && isRenderableSignatureSrc(v)) return v.trim();
  if (!isRecord(v)) return null;
  const data = typeof v.data === 'string' && isRenderableSignatureSrc(v.data) ? v.data.trim() : null;
  if (data) return data;
  const s3Url = typeof v.s3Url === 'string' && isRenderableSignatureSrc(v.s3Url) ? v.s3Url.trim() : null;
  if (s3Url) return s3Url;
  const imageDataUrl = typeof v.imageDataUrl === 'string' && isRenderableSignatureSrc(v.imageDataUrl) ? v.imageDataUrl.trim() : null;
  if (imageDataUrl) return imageDataUrl;
  return null;
}

function looksLikeSignaturePayload(value: unknown): boolean {
  return extractSignatureImageSrc(value) != null || signatureHasStorageRef(value);
}

function signatureHasStorageRef(value: unknown): boolean {
  const v = unwrapMaybeJsonString(value);
  if (!isRecord(v)) return false;
  return typeof v.s3Key === 'string' && v.s3Key.length > 0;
}

function formatTableCell(
  col: FormSubmissionColumn,
  value: unknown,
  submittedAt: string | undefined,
  formFields: FormField[]
): string {
  if (col.type === 'datetime' || col.fieldId === '_submittedAt') {
    return formatAdminBusinessEt(
      typeof value === 'string' ? value : typeof submittedAt === 'string' ? submittedAt : undefined
    );
  }
  if (col.type === 'appointment' || looksLikeAppointmentValue(value)) {
    const field = formFields.find((f) => f.id === col.fieldId);
    const tz = scheduleTimeZoneFromField(field);
    return formatAppointmentCell(unwrapMaybeJsonString(value), tz);
  }
  const unwrapped = unwrapMaybeJsonString(value);
  if (col.type === 'signature' || looksLikeSignaturePayload(unwrapped)) {
    return extractSignatureImageSrc(unwrapped) ? 'Signature captured' : 'Signature (stored)';
  }
  if (col.type === 'address' || looksLikeAddressShape(unwrapped)) {
    return formatAddressForTable(unwrapped);
  }
  return formatScalarForTable(unwrapped);
}

function humanizeKey(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase());
}

function fieldDisplayLabel(field: FormField | undefined, fieldId: string): string {
  if (field?.label?.trim()) return field.label.trim();
  if (fieldId.startsWith('_')) return humanizeKey(fieldId.replace(/^_/, ''));
  return fieldId;
}

function SignatureAnswerDisplay({ value }: { value: unknown }) {
  const src = extractSignatureImageSrc(value);
  if (src) {
    return (
      <div className="space-y-2">
        <a
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block max-w-full rounded-lg border border-border bg-white p-2 shadow-sm transition-opacity hover:opacity-95 dark:bg-zinc-950"
          title="Open signature in a new tab"
        >
          <img
            src={src}
            alt="Submitted signature"
            className="max-h-52 max-w-full min-h-[80px] min-w-[160px] object-contain"
            loading="lazy"
          />
        </a>
        <p className="text-xs text-muted-foreground">
          Raster preview of the captured signature. Click the image to open it full size (if your browser allows data
          URLs in a new tab).
        </p>
      </div>
    );
  }

  const v = unwrapMaybeJsonString(value);
  if (isRecord(v) && typeof v.s3Key === 'string') {
    return (
      <div className="space-y-1 text-sm">
        <p className="text-muted-foreground">Signature is stored as a file; no image URL was returned with this submission.</p>
        <p className="font-mono text-xs break-all text-foreground/80">{v.s3Key}</p>
      </div>
    );
  }

  return <span className="text-muted-foreground text-sm">No signature image available for preview.</span>;
}

function formatAnswerDetail(field: FormField | undefined, value: unknown): ReactNode {
  if (value === null || value === undefined || value === '') {
    return <span className="text-muted-foreground">—</span>;
  }

  const type = field?.type ?? '';
  const v = unwrapMaybeJsonString(value);

  if (type === 'appointment' || looksLikeAppointmentValue(v)) {
    if (!isRecord(v)) return formatScalarForTable(value);
    const o = v as Record<string, unknown>;
    const start = typeof o.start === 'string' ? o.start : null;
    const end = typeof o.end === 'string' ? o.end : null;
    const slotId = typeof o.slotId === 'string' ? o.slotId : null;
    const tz = resolveScheduleTimeZone(
      typeof o.scheduleTimeZone === 'string' ? o.scheduleTimeZone : undefined,
      scheduleTimeZoneFromField(field)
    );
    return (
      <div className="space-y-1">
        {start ? (
          <p className="font-medium text-foreground">
            {end ? formatAppointmentRangeInZone(start, end, tz) : formatIsoInScheduleZone(start, tz)}
          </p>
        ) : null}
        {slotId ? (
          <p className="font-mono text-xs text-muted-foreground">
            Slot ID: {slotId}
          </p>
        ) : !start ? (
          <span className="font-mono text-sm">{formatScalarForTable(o)}</span>
        ) : null}
      </div>
    );
  }

  if (type === 'fullName' && isRecord(v)) {
    const o = v as Record<string, unknown>;
    const parts = ['prefix', 'first', 'middle', 'last', 'suffix']
      .map((k) => (typeof o[k] === 'string' ? (o[k] as string).trim() : ''))
      .filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : <pre className="text-xs">{JSON.stringify(v, null, 2)}</pre>;
  }

  if (type === 'address' && isRecord(v)) {
    const o = v as Record<string, unknown>;
    const lines = ['street', 'street2', 'city', 'state', 'zip', 'country']
      .map((k) => (typeof o[k] === 'string' ? (o[k] as string).trim() : ''))
      .filter(Boolean);
    return lines.length > 0 ? (
      <div className="whitespace-pre-line">{lines.join('\n')}</div>
    ) : (
      <pre className="text-xs">{JSON.stringify(v, null, 2)}</pre>
    );
  }

  if (type === 'multipleChoice' && Array.isArray(v)) {
    return (
      <ul className="list-inside list-disc space-y-0.5">
        {v.map((x, i) => (
          <li key={i}>{String(x)}</li>
        ))}
      </ul>
    );
  }

  if (type === 'longText' || type === 'paragraph') {
    const s = typeof v === 'string' ? v : JSON.stringify(v);
    return <p className="whitespace-pre-wrap break-words">{s}</p>;
  }

  if (type === 'richText' && typeof v === 'string') {
    return (
      <div
        className="prose prose-sm dark:prose-invert max-w-none break-words"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(v) }}
      />
    );
  }

  if (type === 'payment' && isRecord(v)) {
    const o = v as Record<string, unknown>;
    const rows: [string, string][] = [];
    for (const k of ['status', 'amountUsd', 'currency', 'squarePaymentId', 'last4', 'cardBrand']) {
      if (o[k] != null && o[k] !== '') rows.push([humanizeKey(k), String(o[k])]);
    }
    if (rows.length === 0) {
      return <pre className="text-xs">{JSON.stringify(v, null, 2)}</pre>;
    }
    return (
      <dl className="grid gap-1">
        {rows.map(([k, val]) => (
          <div key={k} className="grid grid-cols-[minmax(0,7rem)_1fr] gap-2 text-sm">
            <dt className="text-muted-foreground">{k}</dt>
            <dd className="break-all font-mono text-xs">{val}</dd>
          </div>
        ))}
      </dl>
    );
  }

  if (type === 'signature' || looksLikeSignaturePayload(v)) {
    return <SignatureAnswerDisplay value={value} />;
  }

  if (type === 'fileUpload' && isRecord(v)) {
    const files = (v as { files?: unknown[] }).files;
    if (Array.isArray(files)) {
      return (
        <ul className="space-y-1 text-sm">
          {files.map((f, i) => (
            <li key={i} className="font-mono text-xs">
              {isRecord(f) ? String(f.originalName ?? f.storageKey ?? JSON.stringify(f)) : String(f)}
            </li>
          ))}
        </ul>
      );
    }
  }

  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'number') return String(v);
  if (typeof v === 'string') return <span className="break-words">{v}</span>;

  if (Array.isArray(v)) {
    return (
      <ul className="list-inside list-disc space-y-0.5 text-sm">
        {v.map((x, i) => (
          <li key={i}>{typeof x === 'object' ? JSON.stringify(x) : String(x)}</li>
        ))}
      </ul>
    );
  }

  if (isRecord(v)) {
    const entries = Object.entries(v);
    return (
      <dl className="grid gap-2 border-l-2 border-muted pl-3">
        {entries.map(([k, v]) => (
          <div key={k}>
            <dt className="text-xs text-muted-foreground">{humanizeKey(k)}</dt>
            <dd className="break-words text-sm">
              {v != null && typeof v === 'object' ? JSON.stringify(v) : String(v ?? '—')}
            </dd>
          </div>
        ))}
      </dl>
    );
  }

  return String(v);
}

const SUBMITTER_HIDDEN_KEYS = new Set(['userId', 'user_id', 'userid']);

function isSubmitterKeyHidden(key: string): boolean {
  return SUBMITTER_HIDDEN_KEYS.has(key) || SUBMITTER_HIDDEN_KEYS.has(key.toLowerCase());
}

function pickSubmitterValue(submitter: Record<string, unknown>, keys: string[]): string | null {
  for (const k of keys) {
    const v = submitter[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return null;
}

function submitterCoreFields(submitter: Record<string, unknown> | undefined): {
  name: string | null;
  email: string | null;
  phone: string | null;
} {
  if (!submitter) return { name: null, email: null, phone: null };
  const name = pickSubmitterValue(submitter, ['fullName', 'full_name', 'name', 'displayName', 'firstName', 'lastName']);
  const email = pickSubmitterValue(submitter, ['email', 'Email']);
  const phone = pickSubmitterValue(submitter, ['cellPhoneNumber', 'cell_phone', 'phone', 'Phone', 'contactPhone']);
  return { name, email, phone };
}

/** Cell text that matches header submitter info (name, email, …) — omit from card preview grid. */
function submitterHeaderDuplicateDisplays(submitter: Record<string, unknown> | undefined): Set<string> {
  const out = new Set<string>();
  if (!submitter) return out;
  for (const [k, v] of Object.entries(submitter)) {
    if (isSubmitterKeyHidden(k)) continue;
    if (typeof v === 'string' && v.trim()) out.add(v.trim().toLowerCase());
  }
  return out;
}

function isEmptySubmissionCellDisplay(display: string): boolean {
  const t = display.trim();
  return t === '' || t === '—' || t === '-' || t.toLowerCase() === 'n/a';
}

/** List columns omitted from card preview: submitted-at column, form version, other `system` fields. */
function isHiddenOnSubmissionCardColumn(col: FormSubmissionColumn): boolean {
  if (col.fieldId === '_submittedAt') return true;
  const id = col.fieldId.toLowerCase().replace(/[\s-]/g, '');
  if (id.includes('formversion') || id === 'form_version') return true;
  const lab = typeof col.label === 'string' ? col.label.trim() : '';
  if (/^form\s*version\b/i.test(lab)) return true;
  if (col.system === true) return true;
  return false;
}

/** Detail sheet: use full row width for answers that are usually large or complex. */
function isWideDetailResponseField(field: FormField | undefined): boolean {
  const t = field?.type ?? '';
  return [
    'longText',
    'paragraph',
    'richText',
    'appointment',
    'signature',
    'fileUpload',
    'address',
    'payment',
  ].includes(t);
}

function SubmissionDetailBody({
  detail,
  formFields,
  onDeleteRequest,
}: {
  detail: FormSubmissionDetail;
  formFields: FormField[];
  onDeleteRequest: () => void;
}) {
  const fieldById = useMemo(() => new Map(formFields.map((f) => [f.id, f] as const)), [formFields]);

  const answers = (detail.answers && typeof detail.answers === 'object' && !Array.isArray(detail.answers)
    ? detail.answers
    : {}) as Record<string, unknown>;

  const orderedIds = useMemo(() => {
    const fromDef = formFields.map((f) => f.id);
    const rest = Object.keys(answers).filter((id) => !fromDef.includes(id));
    rest.sort();
    return [...fromDef.filter((id) => id in answers), ...rest];
  }, [formFields, answers]);

  return (
    <div className="space-y-6 text-sm">
      <div className="flex flex-wrap gap-2">
        {detail.status ? (
          <Badge variant="secondary" className="capitalize">
            {String(detail.status)}
          </Badge>
        ) : null}
        {detail.formVersion != null ? (
          <Badge variant="outline">Form version {detail.formVersion}</Badge>
        ) : null}
      </div>

      <section>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Submitted</h3>
        <p className="mt-1 text-base">
          {formatAdminBusinessEt(typeof detail.submittedAt === 'string' ? detail.submittedAt : undefined)}
        </p>
      </section>

      {detail.submitter &&
      Object.entries(detail.submitter as Record<string, unknown>).some(([k]) => !isSubmitterKeyHidden(k)) ? (
        <>
          <Separator />
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Submitter</h3>
            <dl className="mt-2 grid gap-2">
              {(() => {
                const core = submitterCoreFields(detail.submitter as Record<string, unknown>);
                const rows: Array<[string, string]> = [];
                if (core.name) rows.push(['Name', core.name]);
                if (core.email) rows.push(['Email', core.email]);
                if (core.phone) rows.push(['Phone number', core.phone]);
                return rows.length > 0 ? (
                  rows.map(([k, v]) => (
                    <div key={k} className="grid grid-cols-1 gap-0.5 sm:grid-cols-[8rem_1fr] sm:gap-3">
                      <dt className="text-muted-foreground">{k}</dt>
                      <dd className="break-all font-medium">{v}</dd>
                    </div>
                  ))
                ) : (
                  <p className="text-muted-foreground">—</p>
                );
              })()}
            </dl>
          </section>
        </>
      ) : null}

      <Separator />
      <section>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Responses</h3>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {orderedIds.length === 0 ? (
            <p className="text-muted-foreground sm:col-span-2">No answers recorded.</p>
          ) : (
            orderedIds.map((fieldId) => {
              const field = fieldById.get(fieldId);
              const label = fieldDisplayLabel(field, fieldId);
              const value = answers[fieldId];
              return (
                <div
                  key={fieldId}
                  className={cn(
                    'rounded-lg border border-border/60 bg-muted/20 px-3 py-3',
                    isWideDetailResponseField(field) && 'sm:col-span-2'
                  )}
                >
                  <p className="text-xs font-medium text-muted-foreground">{label}</p>
                  {field?.type ? (
                    <p className="mt-0.5 text-[10px] uppercase tracking-wide text-muted-foreground/80">
                      {field.type}
                    </p>
                  ) : null}
                  <div className="mt-2">{formatAnswerDetail(field, value)}</div>
                </div>
              );
            })
          )}
        </div>
      </section>

      {detail.meta && isRecord(detail.meta) && Object.keys(detail.meta).length > 0 ? (
        <>
          <Separator />
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Technical meta</h3>
            <dl className="mt-2 grid gap-2">
              {Object.entries(detail.meta).map(([k, v]) => (
                <div key={k} className="grid grid-cols-1 gap-0.5 sm:grid-cols-[8rem_1fr] sm:gap-3">
                  <dt className="text-muted-foreground">{humanizeKey(k)}</dt>
                  <dd className="break-all font-mono text-xs">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</dd>
                </div>
              ))}
            </dl>
          </section>
        </>
      ) : null}

      <Separator />
      <Button type="button" variant="destructive" size="sm" className="gap-2" onClick={onDeleteRequest}>
        <Trash2 className="h-4 w-4" />
        Delete submission
      </Button>
    </div>
  );
}

type Props = {
  formId: string;
  publishedVersion?: number;
  /** Draft (or latest) definition fields — used to label answers and format values in the detail sheet. */
  formFields?: FormField[];
  onSubmissionsMutated?: () => void;
};

export function FormSubmissionsPanel({ formId, publishedVersion, formFields = [], onSubmissionsMutated }: Props) {
  const [searchParams] = useSearchParams();
  const deepLinkSubmissionId = searchParams.get('submission');
  const [data, setData] = useState<FormSubmissionsListData | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const limit = 25;
  const [statusFilter, setStatusFilter] = useState<'all' | 'completed' | 'spam' | 'abandoned'>('all');

  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<FormSubmissionDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const deepLinkOpenedRef = useRef(false);

  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [listView, setListView] = useState<'cards' | 'table'>(() => {
    try {
      const v = localStorage.getItem(SUBMISSIONS_LIST_VIEW_KEY);
      if (v === 'table' || v === 'cards') return v;
    } catch {
      /* ignore */
    }
    return 'cards';
  });

  useEffect(() => {
    try {
      localStorage.setItem(SUBMISSIONS_LIST_VIEW_KEY, listView);
    } catch {
      /* ignore */
    }
  }, [listView]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dynamicFormsApi.listSubmissions(formId, {
        page,
        limit,
        status: statusFilter === 'all' ? undefined : statusFilter,
      });
      if (res.statusCode !== 200) {
        throw new Error(res.message || 'Failed to load submissions');
      }
      setData(res.data);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to load submissions');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [formId, page, statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  const openDetail = async (submissionId: string) => {
    setSelectedId(submissionId);
    setSheetOpen(true);
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await dynamicFormsApi.getSubmission(formId, submissionId);
      if (res.statusCode !== 200) {
        throw new Error(res.message || 'Failed to load submission');
      }
      setDetail(res.data.submission);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to load submission');
    } finally {
      setDetailLoading(false);
    }
  };

  // Deep-link from notifications: /f/:formId/submissions?submission=...
  useEffect(() => {
    if (!deepLinkSubmissionId || deepLinkOpenedRef.current || loading) return;
    deepLinkOpenedRef.current = true;
    void openDetail(deepLinkSubmissionId);
  }, [deepLinkSubmissionId, loading, formId]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const removedId = deleteTarget;
    setDeleting(true);
    try {
      const res = await dynamicFormsApi.deleteSubmission(formId, removedId);
      if (res.statusCode !== 200) {
        throw new Error(res.message || 'Failed to delete');
      }
      toast.success('Submission deleted');
      setDeleteTarget(null);
      if (selectedId === removedId) {
        setSheetOpen(false);
        setSelectedId(null);
        setDetail(null);
      }
      await load();
      onSubmissionsMutated?.();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadFormSubmissionsCsv(formId, {
        definitionVersion: typeof publishedVersion === 'number' ? publishedVersion : undefined,
      });
      toast.success('Download started');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const columns = data?.columns ?? [];
  const rows = data?.rows ?? [];
  const total = data?.pagination?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
        <div className="grid gap-1">
          <Label>Status</Label>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
            <SelectTrigger className="w-[200px] bg-background">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="spam">Spam</SelectItem>
              <SelectItem value="abandoned">Abandoned</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="grid gap-1">
            <Label className="text-muted-foreground">Layout</Label>
            <div className="flex h-10 rounded-lg border border-border/70 bg-muted/30 p-0.5">
              <Button
                type="button"
                variant={listView === 'cards' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-9 gap-1.5 px-3"
                onClick={() => setListView('cards')}
              >
                <LayoutGrid className="h-4 w-4" />
                Cards
              </Button>
              <Button
                type="button"
                variant={listView === 'table' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-9 gap-1.5 px-3"
                onClick={() => setListView('table')}
              >
                <Table2 className="h-4 w-4" />
                Table
              </Button>
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-10 gap-2"
            disabled={exporting || total === 0}
            onClick={() => void handleExport()}
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Export CSV
          </Button>
        </div>
      </div>

      {/* <p className="text-xs text-muted-foreground">
        Columns follow the published definition (or version {data?.columnsVersion ?? '—'}).{' '}
        {data?.columnMode ? <span className="font-mono">mode={data.columnMode}</span> : null}
      </p> */}

      {listView === 'cards' ? (
        <div className="space-y-3">
          {loading ? (
            <div className="flex justify-center rounded-xl border border-border/60 py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded-xl border border-border/60 py-14 text-center text-sm text-muted-foreground">
              No submissions match this filter.
            </div>
          ) : (
            rows.map((row) => {
              const submitter = row.submitter as Record<string, unknown> | undefined;
              const title = submitterCardTitle(submitter);
              const subtitle = submitterCardSubtitle(submitter);
              const headerDupes = submitterHeaderDuplicateDisplays(submitter);
              const cardEligibleCols = columns.filter((c) => !isHiddenOnSubmissionCardColumn(c));
              const substantiveCols = cardEligibleCols.filter((c) => {
                const display = formatTableCell(
                  c,
                  row.values[c.fieldId],
                  row.submittedAt,
                  formFields
                ).trim();
                if (isEmptySubmissionCellDisplay(display)) return false;
                return !headerDupes.has(display.toLowerCase());
              });
              const previewCols = substantiveCols.slice(0, 6);
              const moreInDetail = Math.max(0, substantiveCols.length - previewCols.length);

              return (
                <Card key={row.submissionId} className="border-border/70 shadow-sm">
                  <CardContent className="space-y-4 px-4 sm:px-5">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0 flex-1 space-y-1">
                        <p className="text-base font-semibold leading-snug text-foreground">{title}</p>
                        {subtitle ? (
                          <p className="text-sm text-muted-foreground break-words">{subtitle}</p>
                        ) : null}
                        <div className="flex flex-wrap items-center gap-2 pt-0.5">
                          <span className="text-xs text-muted-foreground">
                            {formatAdminBusinessEt(row.submittedAt)}
                          </span>
                          {row.status ? (
                            <Badge variant="secondary" className="text-[10px] capitalize">
                              {row.status}
                            </Badge>
                          ) : null}
                        </div>
                      </div>
                      <div className="flex shrink-0 flex-wrap gap-2 sm:justify-end">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="gap-2"
                          onClick={() => void openDetail(row.submissionId)}
                        >
                          <Eye className="h-4 w-4" />
                          View
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="gap-2 text-muted-foreground hover:text-destructive"
                          onClick={() => setDeleteTarget(row.submissionId)}
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </Button>
                      </div>
                    </div>
                    {previewCols.length > 0 ? (
                      <div className="grid gap-2 sm:grid-cols-3">
                        {previewCols.map((col) => {
                          const display = formatTableCell(
                            col,
                            row.values[col.fieldId],
                            row.submittedAt,
                            formFields
                          );
                          return (
                            <div
                              key={col.fieldId}
                              className="rounded-lg border border-border/60 bg-muted/15 px-3 py-2.5"
                            >
                              <p className="text-[11px] font-medium leading-snug text-muted-foreground">{col.label}</p>
                              <p className="mt-1.5 text-sm font-medium leading-snug text-foreground break-words">
                                {display}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                    {moreInDetail > 0 ? (
                      <p className="text-xs text-muted-foreground">
                        +{moreInDetail} more field{moreInDetail === 1 ? '' : 's'} in details
                      </p>
                    ) : null}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      ) : (
      <div className="rounded-xl border border-border/60 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="sticky left-0 z-10 w-20 min-w-20 max-w-20 bg-muted/80 px-1 text-center text-[10px] font-semibold uppercase tracking-wide text-muted-foreground backdrop-blur-sm">
                  Actions
                </TableHead>
                {columns.map((col) => (
                  <TableHead
                    key={col.fieldId}
                    className={cn(
                      'h-auto min-h-10 align-top whitespace-normal py-2 text-left text-[10px] font-semibold leading-tight',
                      SUBMISSIONS_DATA_COL_CLASS,
                      col.system && 'text-muted-foreground'
                    )}
                    title={col.label}
                  >
                    <span className="block line-clamp-2 break-words hyphens-auto">{col.label}</span>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={Math.max(columns.length + 1, 2)} className="h-28 text-center">
                    <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={Math.max(columns.length + 1, 2)}
                    className="h-20 text-center text-sm text-muted-foreground"
                  >
                    No submissions match this filter.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.submissionId}>
                    <TableCell className="sticky left-0 z-10 w-20 min-w-20 max-w-20 bg-background px-1 align-top pt-1.5">
                      <div className="inline-flex shrink-0 items-center justify-center gap-0">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-muted-foreground hover:text-foreground"
                              aria-label="View submission"
                              onClick={() => void openDetail(row.submissionId)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">View submission</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              aria-label="Delete submission"
                              onClick={() => setDeleteTarget(row.submissionId)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">Delete submission</TooltipContent>
                        </Tooltip>
                      </div>
                    </TableCell>
                    {columns.map((col) => {
                      const display = formatTableCell(col, row.values[col.fieldId], row.submittedAt, formFields);
                      return (
                        <TableCell
                          key={col.fieldId}
                          className={cn(
                            'align-top whitespace-normal text-sm leading-snug',
                            SUBMISSIONS_DATA_COL_CLASS
                          )}
                          title={display}
                        >
                          <span className="line-clamp-4 break-words">{display}</span>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      )}

      {!loading && total > 0 ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground">
            Page {page} of {totalPages} · {total} total
          </p>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-4 p-6 pt-10 pr-12 sm:max-w-2xl"
        >
          <SheetHeader className="space-y-1 p-0 text-left">
            <SheetTitle>Submission details</SheetTitle>
            <SheetDescription className="sr-only">
              Review submitted answers and submitter information for this form response.
            </SheetDescription>
          </SheetHeader>
          <div className="min-h-0 flex-1 overflow-y-auto pb-2">
            {detailLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : detail ? (
              <SubmissionDetailBody
                detail={detail}
                formFields={formFields}
                onDeleteRequest={() => selectedId && setDeleteTarget(selectedId)}
              />
            ) : (
              <p className="text-muted-foreground">Could not load details.</p>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this submission?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the submission and related appointment booking handling. The form&apos;s submission count will
              update. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleting}
              onClick={(e) => {
                e.preventDefault();
                void handleDelete();
              }}
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
